"use client";

import { gql } from "graphql-request";
import { useQuery } from "@tanstack/react-query";
import { createGraphQLClient } from "@/services/graphql.service";
import { useMemo } from "react";
import { useEntitiesFromDb } from "./schema/use-entities-from-db";
import { resolveFeatureEntity } from "@/services/template.service";
import { Language } from "./use-languages";

const customerConfigQuery = gql`
  query CustomerConfig {
    customerConfigList(pagination: { page: 1, per_page: 1 }) {
      data {
        id
        default_language {
          data {
            id
            name
            locale
          }
        }
      }
    }
  }
`;

interface CustomerConfig {
  id: string;
  default_language?: {
    data: Language;
  };
}

interface CustomerConfigResponse {
  customerConfigList: {
    data: CustomerConfig[];
  };
}

export const useCustomerConfig = () => {
  const { entities } = useEntitiesFromDb();

  // Languages are opt-in, so `default_language` is absent from the generated
  // schema until that feature is enabled — selecting it anyway fails the query.
  const hasDefaultLanguage = useMemo(
    () =>
      resolveFeatureEntity("CustomerConfig", entities)?.entity_relations?.data?.some(
        (relation) => relation.field_name === "default_language",
      ) ?? false,
    [entities],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["customer-config"],
    queryFn: async () => {
      const client = createGraphQLClient({
        endpointName: "customerConfigList",
      });
      const response = await client.request<CustomerConfigResponse>(customerConfigQuery);
      return response.customerConfigList.data[0];
    },
    enabled: hasDefaultLanguage,
    // TODO: Invalidate on config changes from other users (e.g., via websocket event)
    staleTime: 20 * 60 * 1000, // 20 minutes
  });

  const defaultLanguage = useMemo(() => data?.default_language?.data, [data]);
  const localeFieldId = useMemo(
    () =>
      resolveFeatureEntity("Language", entities)?.entity_fields?.data?.find(
        (x) => x.name === "locale",
      )?.id,
    [entities],
  );

  return {
    defaultLanguage,
    localeFieldId,
    customerConfig: data,
    isLoading,
    error,
  };
};
